<template>
  <ModalDialog
    ref="dialog"
    title="Add Direct Payment"
    :submitting="submitting"
    :submitDisabled="submitting || !canSubmitPayment"
    submitTitle="Add Payment"
    icon="fa-credit-card"
    :scrollable="false"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm">
      <FormRow label="Order">
        <FormStaticLabel>{{order.sequenceNumber}}</FormStaticLabel>
      </FormRow>
      <FormRow label="Order Total">
        <FormStaticLabel>
          <strong>{{formatCurrency(order.totalPrice)}}</strong>
        </FormStaticLabel>
      </FormRow>
      <FormRow label="Payments Made" v-if="paymentsMadeList.length > 0">
        <FormStaticLabel>
          {{formatCurrency(paymentsMadeTotal)}}
        </FormStaticLabel>
      </FormRow>
      <FormRow label="Amount Due" v-if="paymentsMadeList.length > 0">
        <FormStaticLabel>
          <strong>{{formatCurrency(amountDue)}}</strong>
        </FormStaticLabel>
      </FormRow>
      <div>

        <div v-if="!loading && !loadingError">
          <FormRow label="Type">
            <FormSelect :options="PaymentTypeLookup" v-model="payment.paymentType" field="paymentType" />
          </FormRow>
          <FormRow label="Reference" :fieldCount="2">
            <template v-slot:field1>
              <FormInputText v-model="payment.reference" placeholder="Payment Reference" field="reference" />
            </template>
            <template v-slot:field2>
              <FormInputText v-model="payment.description" placeholder="Additional description" field="description" />
            </template>
          </FormRow>
          <FormRow label="Amount" :fieldCount="2">
            <template v-slot:field1>
              <FormInputCurrency ref="paymentAmount" v-model="payment.amount" field="amount" />
            </template>
            <template v-slot:field2>
              <FormDate v-model="payment.date" placeholder="Payment Date" field="date" />
            </template>
          </FormRow>

          <div class="mt-4" />

          <div v-if="payment.amount > amountDue">
            <Icon iconStyle="fas" icon="fa-exclamation-circle" iconClass="result-fg-failed" class="mr-1" /> Payment amount is <strong>over</strong> the order total amount due
          </div>
          <div v-if="payment.amount > 0 && payment.amount < amountDue">
            <Icon iconStyle="fas" icon="fa-exclamation-circle" iconClass="result-fg-notice" class="mr-1" /> Payment amount is <strong>under</strong> the order total amount due
          </div>

        </div>
      </div>
    </FormGroup>
  </ModalDialog>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import { PaymentTypes, PaymentTypeLookup, OrderReservedPaymentStatusTypes } from '@/store/types'

export default {
  name: 'AddOrderPaymentDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
  },
  props: {
    orderId: Number
  },
  data() {
    return {
      dialogForm: {},
      payment: { },
      order: {},
      orderReservedPaymentList: [],
      loading: false,
      loadingError: '',
      submitting: false,
    }
  },
  created() {
    this.PaymentTypes = PaymentTypes
    this.PaymentTypeLookup = PaymentTypeLookup    
  },
  mounted() {
    this.requestData()
  },
  computed: {
    canSubmitPayment() {
      if(!this.orderId)
        return false
      if(this.currentPayment == 0)
        return false
      if(this.currentPayment > this.order.totalPrice)
        return false

      return true
    },
    currentPayment() {
      return this.parseDecimal(this.payment.amount)
    },
    paymentsMadeList() {
      return this.orderReservedPaymentList.filter(reservedPayment => {
        return (reservedPayment.status == OrderReservedPaymentStatusTypes.available)
      })
    },
    paymentsMadeTotal() {
      return this.paymentsMadeList.reduce((curAmount, reservedPayment) => {
        return this.parseDecimal(curAmount + reservedPayment.reservedAmount)
      }, 0)
    },
    amountDue() {
      return Utils.roundCurrency(this.order.totalPrice - this.paymentsMadeTotal)
    }
  },
  methods: {
    show(options = {}) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      // Defaults
      this.payment = {
        paymentType: PaymentTypes.creditCard,
        amount: 0,
        date: moment().local().toISOString(),
        reference: '',
        description: ''
      }

      this.$nextTick(() => {
        if(this.$refs.paymentAmount) {
          this.$refs.paymentAmount.select()
          this.$refs.paymentAmount.focus()
        }
      })

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      let paymentDisplay = Utils.formatCurrency(this.payment.amount)
      let contactName = this.order.contact.name
      
      var message = `Confirm payment of <strong>${paymentDisplay}</strong> received by <strong>${contactName}</strong>?`
      this.confirmMessage('Confirm Payment?', null, { html: message })
      .then(success => {
        if(success) {
          this._submitPayment()
        }
      })
    },
    _submitPayment() {
      var createPaymentData = {
        paymentType: this.payment.paymentType,
        amount: Utils.roundCurrency(parseFloat(this.payment.amount)),
        date: this.payment.date,
        reference: this.payment.reference,
        description: this.payment.description
      }

      this.submitting = true
      this.$apiService.ordersService.createOrderPayment(this.orderId, createPaymentData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },

    requestData() {
      this.order = {}
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.ordersService.getOrderPayments(this.orderId),
        this.$apiService.ordersService.getOrder(this.orderId)
      ])
      .then(results => {
        this.orderReservedPaymentList = results[0].data
        this.order = results[1].data
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    }
  }
}
</script>